import React, { useRef, useState, useImperativeHandle, useEffect } from "react";
import { TransitionGroup } from "react-transition-group";
import { useHistory } from "react-router-dom";
import { Grid } from "@mui/material";
import ProspectFormBox from "@icoach/nonMember/prospect/ProspectFormBox";
import { NON_MEMBER_ROUTER } from "@icoach/router/routerPath"
import useCustomerGuestApi from "@apis/useCustomerGuestApi";
import { refIsRequiredError, isEmpty } from "@util/utils";
import { parseDate } from "@util/moment";
import { EmployeeSelect } from "@icoach/components/";
import { Icons, Card, Typography, Button, Alert, Collapse, DateField, IconButton, Box } from "@curves/ui-base";

/**
 * introducerInfoRef: 傳入的ref，取得多筆名單的介紹人資訊 同層介入取得(盡量少用不好閱讀)
 * sourceOptions: 名單來源選單
 * requiredOptions: 名單需求選單
 */
// 新增一筆名單
const OtherProspectForm = React.forwardRef((props, ref) => {
	const { introducerInfoRef, sourceOptions, requiredOptions } = props;
	const [isOther, setOther] = React.useState(0);
	const [deleteIdx, setDeleteIdx] = React.useState([]);
	const [introducerInfoState, setIntroducerInfo] = useState({});
	const otherRef = useRef([]);

	useImperativeHandle(
		ref,
		() => ({
			isNull: () => otherRef.current.every(item => item === null),
			isError: () => {
				let err = false;
				otherRef.current.forEach(item => {
					if (item) {
						const target = item.isError();
						if (target) err = target;
					}
				});
				return err;
			},
			getResult: () => {
				return otherRef.current.reduce((acc, cur) => {
					let newAry = [...acc];
					if (cur) newAry.push(cur.getResult());
					return newAry;
				}, []);
			},
		}),
		// eslint-disable-next-line
		[]
	);

	const handleRemoveFriendsClick = id => e => {
		let newState = [...deleteIdx, id];
		if (isOther !== 0 && isOther === deleteIdx.length + 1) newState = [];
		if (newState.length === 0) {
			setOther(0);
			otherRef.current = [];
		}
		setDeleteIdx([...newState]);
	};

	/*
	 * 新增一筆名單 (多筆名單使用)
	 **/
	const handleAddOtherClick = e => {
		const introducerInfo = introducerInfoRef.current.getMulti();
		setIntroducerInfo(introducerInfo);
		setOther(pre => pre + 1);
	};

	const { sourceType = null } = introducerInfoState;
	return (
		<React.Fragment>
			<TransitionGroup>
				{Array(isOther)
					.fill()
					.map((x, idx) => {
						if (deleteIdx.indexOf(idx) !== -1) return null;
						return (
							<Collapse key={idx} className="pt-3">
								<Box className="flex flex-direction-row-reverse">
									<IconButton onClick={handleRemoveFriendsClick(idx)} style={{ marginBottom: "-1rem" }}>
										<Icons.Cancel />
									</IconButton>
								</Box>
								<ProspectFormBox ref={el => (otherRef.current[idx] = el)} sourceType={sourceType} sourceOptions={sourceOptions} requiredOptions={requiredOptions} />
							</Collapse>
						);
					})}
			</TransitionGroup>
			<Button className="mt-5" onClick={handleAddOtherClick} variant="outlined" startIcon={<Icons.Plus />}>
				新增一筆名單
			</Button>
		</React.Fragment>
	);
});

const ProspectAddPage = () => {
	const { getProspectOverviewOptionsApi, postAddProspectApi } = useCustomerGuestApi();
	const [isMounted, setMounted] = useState(false);
	const [options, setOptions] = useState({
		sourceOptions: [],
		requiredOptions: [],
		employeeOptions: [],
	});
	const history = useHistory();
	const checkInDateRef = useRef(null);
	const coachRef = useRef(null);
	const prospectFormRef = useRef(null);
	const fieldListGroupRef = useRef(null);

	const handleBackToOverviewOnClick = e => {
		history.push(NON_MEMBER_ROUTER.PROSPECT.OVERVIEW);
	};

	const handleAddOnClick = e => {
		let params = [];
		if (!refIsRequiredError(checkInDateRef, prospectFormRef, coachRef, fieldListGroupRef)) {
			const employeeID = coachRef.current.getResult();
			const checkInDate = checkInDateRef.current.getResult();
			params = [{ ...prospectFormRef.current.getResult() }, ...(fieldListGroupRef.current?.getResult() ?? [])];
			params = params.map(x => ({ ...x, employeeID, registrationDate: checkInDate }));
			doAddApi(params);
		}
	};

	const initOptionApi = async () => {
		const resp = await getProspectOverviewOptionsApi();
		if (!isEmpty(resp)) {
			setOptions(resp);
			setMounted(true);
		}
	};

	const doAddApi = async params => {
		const resp = await postAddProspectApi(params);
		if (resp) {
			history.push(NON_MEMBER_ROUTER.PROSPECT.OVERVIEW);
		}
	};

	useEffect(
		() => {
			initOptionApi();
			return () => setMounted(false);
		},
		// eslint-disable-next-line
		[]
	);
	const { sourceOptions = [], requiredOptions = [], employeeOptions = [] } = options;

	return (
		<React.Fragment>
			{isMounted && (
				<Box className="container main-container-spacing">
					<Box component="header" className="page-header">
						<Typography className="page-header-title" variant="h3">
							新增名單
						</Typography>
					</Box>
					<Card>
						<Card.CardContent className="customer-add-apo-header">
							<Grid container>
								<Grid item xs={4}>
									<DateField ref={checkInDateRef} label="登錄日期" defaultValue={parseDate()} required lightColor fullWidth />
								</Grid>
								<Grid item xs={4} />
								<Grid item xs={4}>
									<EmployeeSelect ref={coachRef} options={employeeOptions} name="employeeID" label="擔當" lightColor fullWidth required />
								</Grid>
							</Grid>
						</Card.CardContent>
						<Card.CardContent className="px-3 py-4">
							<Grid container spacing={3}>
								<Grid item xs={12}>
									<Alert color="info" severity="info" variant="filled">
										僅有GP名單、BR名單、自店社群、CP去電這四個來源計算名單數在報表中!
									</Alert>
								</Grid>
								<Grid item xs={12}>
									<ProspectFormBox ref={prospectFormRef} sourceOptions={sourceOptions} requiredOptions={requiredOptions} />
									<OtherProspectForm ref={fieldListGroupRef} introducerInfoRef={prospectFormRef} sourceOptions={sourceOptions} requiredOptions={requiredOptions} />
								</Grid>
							</Grid>
						</Card.CardContent>
						<Card.CardContent className="flex flex-y-flex-end flex-gap-3 pr-4 pb-3">
							<Button variant="outlined" color="secondary" onClick={handleBackToOverviewOnClick}>
								返回
							</Button>
							<Button variant="contained" color="secondary" onClick={handleAddOnClick}>
								新增
							</Button>
						</Card.CardContent>
					</Card>
				</Box>
			)}
		</React.Fragment>
	);
};

export default ProspectAddPage;
